import { DI } from '@intigral/constant/config/DIContainer';
import { IMpxService } from '@intigral/domain/lib/application/services/MpxService';
import { container } from '@intigral/domain/lib/DataDomain';
import { IApiPathManager } from '@intigral/domain/lib/infrastructures/config/ApiPathManager';
import { useMediaQuery } from '@intigral/hooks';
import { GridSkeleton } from '@intigral/shared';
import { SectionHeaderWithPagination } from '@intigral/ui/components/SectionHeaderWithPagination';
import React, { useEffect, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { useLocation } from 'react-router-dom';
import { COMPONENT_TYPES } from '../../config/constants';
import h, { getCountry } from '../../helpers/common';
import formatter from '../../helpers/formatter2';
import { useConfigurationSections } from '../../hooks/useConfiguration';
import { getThinkAnalyticVodListApi } from '../../services/serviceWrapper/thinkAnalyticsWrapper';
import { __ } from '../../translations';
import { transformVOD } from '../../utils/vodTransformer';
import FullVODCarousel from '../elements/FullVODCarousel';
interface ResponseWithEntries {
    entries: Array<any>;
}
interface FullVODCatalogListProps {
    content: any[];
    params?: {
        items_count?: number;
    };
    meta?: {
        show_title_on_listview?: boolean;
        title?: {
            en?: string;
            ar?: string;
        };
    };
    name?: string;
}

export const FullVODCatalogList: React.FC<FullVODCatalogListProps> = ({ content, params, meta, name }) => {
    const [currentContent, setCurrentContent] = useState<any[]>(content || []);
    const [hasMoreItems, setHasMoreItems] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [dataFetched, setDataFetched] = useState<boolean>(false);
    const location = useLocation();
    const pageSize: number = content?.length || Number(params?.items_count) || 10;
    const [rangeFrom, setRangeFrom] = useState<number>(pageSize + 1);
    const [rangeTo, setRangeTo] = useState<number>(rangeFrom + pageSize);
    // Using React Query instead of Redux for configuration
    const { config } = useConfigurationSections();
    const urlParams = new URLSearchParams(window.location.search);
    const sourceParam = urlParams.get('source');
    const isSourceParamInvalid = !sourceParam || ['null', 'undefined'].includes(atob(sourceParam || ''));
    const shouldFetchData = !isSourceParamInvalid && !dataFetched;
    const isMobile = useMediaQuery('(max-width: 768px)');
    const isShowTitle = meta?.show_title_on_listview;
    const pagingId = `component-${Math.random().toString(36).slice(2, 11)}`;
    const paginationClass = `pagination-${pagingId}`;

    const loadMoreItems = async () => {
        if (isLoading || !hasMoreItems) return;
        setIsLoading(true);
        const filter_params = urlParams.get('filter_params') ? atob(urlParams.get('filter_params')!) : '';
        const sort = urlParams.get('sort') ? atob(urlParams.get('sort')!) : '';
        const apiPathManager = container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
        const url = apiPathManager.replacePlaceholders(filter_params);
        const mpxService = container.resolve<IMpxService>(DI.SERVICES.IMpxService);
        const response = (await mpxService.getFeed(url, {
            byCustomValue: `{PC}{${getCountry()}}`,
            range: `${rangeFrom}-${rangeTo}`,
            sort,
        })) as ResponseWithEntries;
        const data = response?.entries?.map((item: any) => transformVOD(item, config?.promotionProviderImages));
        setHasMoreItems(data?.length > 0 || false);
        if (data) {
            setCurrentContent(currentContent.concat(data));
            setRangeFrom(rangeFrom + data.length);
            setRangeTo(rangeTo + data.length);
        }
        setIsLoading(false);
    };

    const loadTAItems = async (source: string) => {
        const TAContent = await getThinkAnalyticVodListApi(
            {
                filter_params: atob(source),
            },
            () => undefined
        );
        setCurrentContent(TAContent ?? []);
    };

    if (shouldFetchData) {
        loadTAItems(sourceParam!);
        setDataFetched(true);
        setHasMoreItems(false);
    }

    useEffect(() => {
        setDataFetched(false);
    }, [location]);

    useEffect(() => {
        setCurrentContent(content);
    }, [content]);

    return (
        <>
            {currentContent !== null && currentContent?.length > 0 && (
                <section
                    style={{ paddingBottom: 'calc(var(--step) * 4', overflow: 'hidden' }}
                    className="full-catalog-list"
                >
                    {isShowTitle && (
                        <SectionHeaderWithPagination
                            title={h.isEnglishLang() ? meta?.title?.en : meta?.title?.ar}
                            className={`${paginationClass}`}
                        />
                    )}
                    {formatter
                        .arrayChunk(currentContent, isShowTitle && !isMobile ? 4 : isMobile ? 3 : 6)
                        .map((rowContent, index) => (
                            <FullVODCarousel
                                key={index}
                                content={rowContent}
                                componentType={COMPONENT_TYPES.FULLVOD_CATALOG_LIST}
                                componentName={name}
                            />
                        ))}
                    {!isShowTitle && (
                        <InView
                            onChange={(inView) => {
                                if (inView) {
                                    loadMoreItems();
                                }
                            }}
                            as="div"
                        >
                            <div
                                style={{ display: isLoading || !hasMoreItems ? 'none' : 'block' }}
                                className="load-more"
                            >
                                <a onClick={loadMoreItems}>{__('load_more')}</a>
                            </div>
                        </InView>
                    )}
                </section>
            )}
            {currentContent !== null && currentContent?.length === 0 && !isShowTitle && (
                <section className="watchlist-notification">{__('vodlist_page_empty_message')}</section>
            )}
            {currentContent === null && <GridSkeleton />}
            {isLoading && <GridSkeleton />}
        </>
    );
};
